import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ScrimMapNavigation.module.css';
import { Round } from '../pages/Scrim';

interface ScrimMapNavigationProps {
  rounds: Round[];
  selectedMap: number | null; // null means "Full video"
  onMapChange: (mapIndex: number | null) => void;
}

const ScrimMapNavigation: React.FC<ScrimMapNavigationProps> = ({
  rounds,
  selectedMap,
  onMapChange
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get unique map indices from rounds
  const availableMaps = useMemo(() => {
    return Array.from(new Set(rounds.map(round => round.map_index))).sort((a, b) => a - b);
  }, [rounds]);

  const currentLabel = selectedMap === null ? 'Full Video' : `Map #${selectedMap}`;

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  const handleSelect = (mapIndex: number | null) => {
    onMapChange(mapIndex);
    setIsMenuOpen(false);
  };

  return (
    <div className={styles.mapDropup} ref={menuRef}>
      <button
        className={styles.mapDropdownButton}
        onClick={() => setIsMenuOpen(prev => !prev)}
        title="Select map"
      >
        {currentLabel}
      </button>
      {isMenuOpen && (
        <div className={styles.mapMenu}>
          <button
            className={`${styles.mapMenuItem} ${selectedMap === null ? styles.active : ''}`}
            onClick={() => handleSelect(null)}
          >
            Full Video
          </button>
          {availableMaps.map(mapIndex => (
            <button
              key={mapIndex}
              className={`${styles.mapMenuItem} ${selectedMap === mapIndex ? styles.active : ''}`}
              onClick={() => handleSelect(mapIndex)}
            >
              {`Map #${mapIndex}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScrimMapNavigation; 